import React from "react";
import { FEATURED_CITIES } from "@/constants/cityConfig";
import { marketPath, slugify, townPath } from "@/components/DirectoryBreadcrumb";
import "./DirectoryMarketCards.scss";
import AppLink from "@/components/AppLink";

export const imageFor = (name, stateCode) =>
  FEATURED_CITIES.find(
    (city) => slugify(city.city) === slugify(name) && String(city.state).toLowerCase() === String(stateCode).toLowerCase(),
  )?.image;

export const marketsFromCities = (cities) =>
  (cities || []).map((city) => ({
    key: city.displayName,
    label: city.displayName,
    href: marketPath(city),
    image: city.image || imageFor(city.city, city.state),
  }));

export const marketsFromFeatured = (featured, stateCode) =>
  (featured || []).map((market) => ({
    key: `${market.stateSlug}/${market.slug}`,
    label: stateCode ? `${market.name}, ${String(stateCode).toUpperCase()}` : market.name,
    href: townPath(market.stateSlug, market.slug),
    image: imageFor(market.name, market.stateSlug),
  }));

export const marketsFromPlaces = (places, limit = 6) => {
  const counts = new Map();
  for (const place of places || []) {
    const city = String(place?.city || "").trim();
    const state = String(place?.state || "").trim().toUpperCase();
    if (!city || !state) continue;
    const key = `${slugify(city)}|${state}`;
    const entry = counts.get(key) || { city, state, count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  }
  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city))
    .slice(0, limit)
    .map(({ city, state }) => ({
      key: `${state}/${slugify(city)}`,
      label: `${city}, ${state}`,
      href: townPath(state.toLowerCase(), slugify(city)),
      image: imageFor(city, state),
    }));
};

export const MarketCardsSkeleton = ({ count = 3 }) => (
  <div className="directory-markets" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <span key={index} className="directory-markets__card is-loading" />
    ))}
  </div>
);

const DirectoryMarketCards = ({ markets, label = "Featured markets" }) =>
  markets?.length ? (
    <ul className="directory-markets" aria-label={label}>
      {markets.map((market) => (
        <li key={market.key}>
          <AppLink className={`directory-markets__card${market.image ? "" : " is-plain"}`} href={market.href}>
            {market.image ? <img src={market.image} alt="" loading="lazy" /> : null}
            <span>{market.label}</span>
          </AppLink>
        </li>
      ))}
    </ul>
  ) : null;

export default DirectoryMarketCards;
